/**
 * **開站內容閘**的前端鏡像（批151 裁示 43／50／99）——開站精靈、代管核可、代管站「同期限改內容」共用。
 *
 * 規則以後端為準、**逐條對拍**（`backend/app/services/activation.py`：`hosted_content_reason`、
 * `hosted_trial_term_ok`；開站的「真的會簽出去的內容」是 `services/provision.planned_entitlements`）：
 *
 * 1. 沒有套裝 → `no_plan`。
 * 2. 付費套裝勾試用 → `custom_content`（試用套裝不勾試用不算：開站照樣簽成試用）。
 * 3. 試用套裝＋期限＝產品的代管試用天數（`hosted_trial_term_days`）→ 期限視為照套裝（不比期限）。
 * 4. 其餘一律交給 **`lib/activation.ts::contentMatchesPlan`**（一般核發同一支，後端也是
 *    `hosted_content_reason` 直接呼叫 `content_matches_plan`），配額恆以一包物件送進去
 *    （`{}`＝明確的空＝偏離）。所以「模板沒寫的鍵」「`{}`」「模組非空才比」「期限總分鐘＋選項」
 *    的處理與一般核發逐字相同——規則只有一份，不會再分岔。
 * 照套裝內容 → admin 照常可做（**不看套裝本身的 `activation_policy`**）；偏離或客製 → 營運主管，
 * 否則後端 403 `role_required`＋`policy_reason`。
 *
 * ★ 這裡算出來的只是**按下去之前的提示**（admin 偏離時提前講、不要按了才 403）；伺服器才是
 *   權威，前端算錯最多是提示不精準，不會放行任何東西。
 * ★ 代理商**不走這支**——它有自己更嚴的 `activation_policy` 閘（見 `ProvisionView`／
 *   `HostedApproveModal` 的 `planDirectOk`）。
 */
import type { PlanSpec, ProductSpec } from '@/api/ls'
import { contentMatchesPlan } from '@/lib/activation'
import { optionOf } from '@/lib/products'
import { LEGACY_COUNT_FIELDS } from '@/lib/seedQuotas'
import { MINUTES_PER_DAY, totalMinutes } from '@/lib/terms'
import { ROLE_RANK, rankOf } from '@/stores/auth'

/** `no_plan`＝沒有套裝（客製）／`custom_content`＝內容偏離套裝。與後端 `policy_reason` 同值。 */
export type HostedContentReason = 'no_plan' | 'custom_content'

/** 偏離時需要的最低角色（與後端 `activation.HOSTED_CONTENT_ROLE` 同值，改一邊要改兩邊）。 */
export const HOSTED_CONTENT_ROLE = 'manager'

export interface HostedContentInput {
  modules?: Record<string, string> | null
  quotas?: Record<string, number | null> | null
  /** `term_days`／`term_minutes` 都是 `null`＝這次不決定期限（同期限改內容的已蓋章證），不比。 */
  term_days?: number | null
  term_minutes?: number | null
  trial?: boolean | null
}

/** 兩個一級相容鍵（席次／產品線上限）——鍵名集中在 `lib/seedQuotas.ts`，本檔不寫產品字面。 */
const LEGACY_KEYS: string[] = Object.values(LEGACY_COUNT_FIELDS)

/**
 * 開站實際會簽出去的 `(modules, quotas)`——`provision.planned_entitlements` 的逐行鏡像。
 *
 * - `modules`：有給（非空）就用給的，否則套裝模板，再否則 `{}`。
 * - `quotas`：非 `null` 就**整包取代**，否則套裝模板；之後只補兩個相容數字鍵（缺鍵時沿用
 *   模板值；判準是**鍵在不在**，值是 `null`＝明確不限，不補）。
 */
export function plannedEntitlements(
  input: HostedContentInput,
  plan: PlanSpec | null | undefined,
): { modules: Record<string, string>; quotas: Record<string, number | null> | null } {
  const given = input.modules ?? {}
  const modules = Object.keys(given).length ? { ...given } : plan ? { ...(plan.modules ?? {}) } : {}
  const planQuotas = plan && plan.quotas != null ? { ...plan.quotas } : null
  let quotas: Record<string, number | null> | null =
    input.quotas != null ? { ...input.quotas } : planQuotas
  if (planQuotas) {
    for (const key of LEGACY_KEYS) {
      if (key in planQuotas && !(quotas && key in quotas)) {
        quotas = { ...(quotas ?? {}), [key]: planQuotas[key] }
      }
    }
  }
  return { modules, quotas }
}

/** 套裝賣出去的是不是試用證（後端 `products.plan_is_trial`：`trial` 旗標或 `kind === 'trial'`）。 */
export function planIsTrial(plan: PlanSpec | null | undefined): boolean {
  return !!plan && (Boolean(plan.trial) || plan.kind === 'trial')
}

/**
 * **代管試用**的期限（後端 `hosted_trial_term_ok`）：試用套裝＋產品的 `hosted_trial_term_days`
 * 視為照套裝——代管試用的預設期限是那個產品選項，不是試用套裝自己的期限。選項不是正整數＝不適用。
 */
export function hostedTrialTermOk(
  spec: ProductSpec | null | undefined,
  plan: PlanSpec | null | undefined,
  termDays: number | null | undefined,
  termMinutes: number | null | undefined,
): boolean {
  if (!plan || !planIsTrial(plan)) return false
  const days: unknown = optionOf(spec, 'hosted_trial_term_days')
  if (typeof days !== 'number' || !Number.isInteger(days) || days <= 0) return false
  if (termDays == null && termMinutes == null) return false
  return totalMinutes(termDays, termMinutes) === days * MINUTES_PER_DAY
}

/**
 * 這份內容需不需要營運主管？`null`＝照套裝內容（admin 照常可做）。
 *
 * `input` 給的是**呼叫端手上的內容**：開站精靈／代管核可給表單值（這裡先照開站規則展開成
 * 真的會簽出去的內容）；同期限改內容給「覆寫後的內容」（後端不展開）。對後者套用展開不會
 * 改變結論：空模組本來就不比（展開成模板也是相等），補進來的一級鍵值就是模板值（比了也相等）。
 */
export function hostedContentReason(
  spec: ProductSpec | null | undefined,
  plan: PlanSpec | null | undefined,
  input: HostedContentInput,
): HostedContentReason | null {
  if (!plan) return 'no_plan'
  if (input.trial && !planIsTrial(plan)) return 'custom_content'
  let termDays = input.term_days ?? null
  let termMinutes = input.term_minutes ?? null
  if (hostedTrialTermOk(spec, plan, termDays, termMinutes)) {
    termDays = null
    termMinutes = null
  }
  const { modules, quotas } = plannedEntitlements(input, plan)
  // 與後端 `hosted_content_reason` 同形：配額 `None` 也送成 `{}`（簽出去就是空的一包）。
  const content = { modules, quotas: quotas ?? {}, term_days: termDays, term_minutes: termMinutes }
  if (!contentMatchesPlan(spec, plan, content)) return 'custom_content'
  return null
}

/** 這個角色送這份內容會不會被內容閘擋下（代理商另有自己的閘，這裡一律回 false）。 */
export function hostedContentBlocked(
  role: string | null | undefined,
  reason: HostedContentReason | null,
): boolean {
  if (reason === null || role === 'reseller') return false
  // 具名比較（不硬編 rank 數字）；未知角色 rank 0 → 擋（fail-closed）。
  return rankOf(role) < ROLE_RANK[HOSTED_CONTENT_ROLE]
}

/**
 * 給畫面看的一句話。`blocked`＝這個角色會被擋（admin）；不會被擋但內容偏離時（營運主管）
 * 也講一聲，讓他知道這是一份客製內容。`action`＝動作名（開站／改內容）。
 */
export function hostedContentHint(
  reason: HostedContentReason | null,
  blocked: boolean,
  action = '開站',
): string | null {
  if (reason === null) return null
  if (blocked) {
    if (reason === 'no_plan') {
      return action === '開站'
        ? '沒有選套裝（客製內容）需要「營運主管」以上才能開站；請選一個套裝照其內容開站。'
        : `這張授權沒有對應的套裝（客製內容），需要「營運主管」以上才能${action}。`
    }
    return action === '開站'
      ? '目前的模組、配額、期限或試用與所選套裝的模板不一致，偏離套裝需要「營運主管」以上才能開站；請改回套裝內容（重新點一次套裝即可帶回模板）。'
      : `改完的內容偏離套裝模板，需要「營運主管」以上才能${action}；改成與套裝模板一致的內容不受限制。`
  }
  return reason === 'no_plan'
    ? `這是沒有套裝的客製內容（營運主管以上才能${action}）。`
    : `內容已偏離套裝的模板（營運主管以上才能${action}）。`
}
